<template>
    <div class="c2p-container">
        <div class="ucp" tabindex="-1">
            <NewCardFormC2PV2
                :card-brands="availableCardBrands"
                :card-number="cardNumber"
                :card-brand="paymentMethodImage"
                :holder="holder"
                :security-code="securityCode"
                :valid-date="validDate"
                :installment-list="installmentList"
                :number-of-installments="numberOfInstallments"
                @input-number="setCardNumber"
                @input-holder="(value) => holder = value"
                @input-security-code="(value) => securityCode = value"
                @input-date="(value) => validDate = value"
                @change-installment="(value) => numberOfInstallments = value"
                :expiry-error="expiryError"
                :card-error="cardError"
                :disable-installment="installmentIsFixed"
            />

            <div class="ucp__agreement-container">
                <CieloCheckBoxV2
                    v-model="rememberMeAgreement"
                    :clickable-slot="true"
                    :text-aria="$ml.get('rememberCards')"
                >
                    <span class="agreement-text" v-text="$ml.get('rememberCards')" />
                </CieloCheckBoxV2>

                <InfoTooltip
                    :items="['rememberCardsTooltipText1', 'rememberCardsTooltipText2']"
                    :orientation="renderMobile() ? 'left' : 'top'"
                />
            </div>

            <div class="ucp__ba-container">
                <h4 class="ucp__title" v-text="$ml.get('billingAddress')" tabindex="0" />

                <SameShippingAddress v-if="productHasShipping" :same-as-shipping="true" />
    
                <NationalBillingAddress :address-errors="addressErrors" />
            </div>

            <div class="ucp__input-container">
                <h4 class="ucp__title" v-text="$ml.get('identityC2P')" tabindex="0" />
                <CieloInputV2
                    :autofocus="false"
                    :borderless="false"
                    name="Identity"
                    :label="$ml.get('identityC2P')"
                    v-model="nationalIdentification"
                    v-validate="isNational ? 'required|numeric|min:11|max:11|cpf-cnpj' : 'required'"
                    :data-vv-as="$ml.get('identityC2P')"
                    :type="isNational ? 'tel' : 'text'"
                    :error="errors.first('Identity')"
                    :mask="isNational ? ['###.###.###-##'] : ''"
                    class="c2p"
                />
            </div>

            <div class="ucp__terms-container">
                <CieloCheckBoxV2
                    v-model="agreement"
                    :clickable-slot="true"
                    :text-aria="$ml.get('termsPreffix') + $ml.get('termsSuffix')"
                >
                    <span v-text="$ml.get('termsPreffix')" />&nbsp;
                    <a target="_blank" :href="showTerms" v-text="$ml.get('termsSuffix')" tabindex="0" />
                </CieloCheckBoxV2>

                <NewCardTermsC2P :brand-def="paymentMethodImage" />
            </div>

            <transition name="fade">
                <CVVRequired
                    v-if="showCVVModal"
                    :security-code="securityCode"
                    @close-modal="closeModal"
                    @submit-cvv="submitCVV"
                    @input-security-code="value => (securityCode = value)"
                />
            </transition>
        </div>
        <ButtonsFinishPaymentV2
            :disable-button-next="disableButtonNext"
            :loading="loading"
            @click-next="finishPayment"
            @click-previous="goToPreviousStep"
        />
    </div>
</template>

<script>
    const CieloInputV2 = () => import(
        /* webpackChunkName: "cielo-input-v2" */
        '@/components/ClickToPay/template/CieloInputV2'
    );

    import vars from '@/config/vars';
    import Swal from 'sweetalert2';
    import loader from '@/helpers/loader';
    import CpfCnpjValidation from '@/helpers/validation-rules/CpfCnpjValidation';
    import ProductHelper from '@/helpers/productHelper';
    import PaymentProcessor from '@/services/payment/PaymentProcessor';
    import { logEvent, logException } from '@/helpers/cieloLogger';
    import { mapGetters } from 'vuex';
    import { isMobile } from '@/helpers/isMobile';
    import NewCardFormC2PV2 from '@/components/ClickToPay/NewCardFormC2PV2.vue';
    import CieloCheckBoxV2 from '@/components/ClickToPay/template/CieloCheckBoxV2';
    import InfoTooltip from '@/components/InfoTooltip';
    import NewCardTermsC2P from '@/components/ClickToPay/NewCardTermsC2P';
    import ButtonsFinishPaymentV2 from '@/components/template/ButtonsFinishPaymentV2';
    import NationalBillingAddress from '@/components/ClickToPay/NationalBillingAddress';
    import SameShippingAddress from '@/components/Billing/SameShippingAddress';
    import CVVRequired from '@/components/ClickToPay/CVVRequired';


export default {
    components: {
        NewCardFormC2PV2,
        CieloCheckBoxV2,
        InfoTooltip,
        NewCardTermsC2P,
        ButtonsFinishPaymentV2,
        NationalBillingAddress,
        SameShippingAddress,
        CieloInputV2,
        CVVRequired,
    },

    data() {
        return {
            isNational: true,
            loading: false,
            expiryError: '',
            cardError: '',
            agreement: false,
            showTerms: `${vars.CIELO_CHECKOUT_URL}/TransactionalVNext/Content/documents/Termos%20e%20condicoes%20de%20Uso_Cielo.pdf`,
            addressErrors: {},
            showCVVModal: false,

        };
    },

    created() {
        loader.multiLanguage = this.$ml;
        // this.$store.state.payment.PaymentMethodId = null;
        // this.$store.commit('cardPayment/resetState');
        this.$validator.extend('cpf-cnpj', CpfCnpjValidation);
    },

    mounted() {
        if (this.cardNumber) {
            this.doBinQuery();
        }

        if (this.securityCode || this.validDate || this.holder || this.cardNumber) {
            this.$validator.validate();
        }

        // this.rememberMeAgreement = false;
    },

    computed: {
        ...mapGetters({
            selectedPaymentMethod: 'payment/getSelectedPaymentMethod',
            getInstallmentListForPaymentMethodSelected: 'payment/getInstallmentListForPaymentMethodSelected',
            sameAsShippingAddress: 'billingAddress/getSameAsShippingAddress',
            installmentIsFixed: 'payment/getSelectedPaymentMethodInstallmentIsAbleToBeFixed',
            billingAddress: 'billingAddress/getBillingAddress',
            isNational: 'billingAddress/getIsNational',
        }),
        
        securityCode: {
            get: self => self.$store.state.cardPayment.SecurityCode,
            set(value) { this.$store.commit('cardPayment/setSecurityCode', value); }
        },
        cardNumber: {
            get: self => self.$store.state.cardPayment.CardNumber || '',
            set(value) { this.$store.commit('cardPayment/setCardNumber', value); }
        },
        holder: {
            get: self => self.$store.state.cardPayment.Holder,
            set(value) { this.$store.commit('cardPayment/setHolder', value); }
        },
        validDate: {
            get() {
                let month = this.$store.state.cardPayment.Expiration.Month;
                let year = this.$store.state.cardPayment.Expiration.Year;

                return `${month}${year}`;
            },
            set(value) {
                const values = value.split('');
                const month = `${values[0]}${values[1]}`;
                const year = `${values[2]}${values[3]}`;
                let expirationMonth = (month >= 0) ? month : '';
                let expirationYear = (year >= 2) ? year : '';

                let today = new Date();
                let cardExpiration = new Date(`20${year}`, month);

                if (month < 1 || month > 12) {
                    this.expiryError = this.$ml.get('dateMonthCardError');
                }
                else if (cardExpiration < today) {
                    this.expiryError = this.$ml.get('cardExpirationError');
                } else {
                    this.expiryError = '';
                }

                this.$store.commit('cardPayment/setExpirationMonth', expirationMonth);
                this.$store.commit('cardPayment/setExpirationYear', expirationYear);
            }
        },
        rememberMeAgreement: {
            get: self => self.$store.state.clickToPayPayment.rememberMeAgreement,
            set(value) { this.$store.state.clickToPayPayment.rememberMeAgreement = value; }
        },
        numberOfInstallments: {
            get: self => self.$store.getters['payment/getInstallmentNumberOfPayments'],
            set(value) { this.$store.commit('payment/setNumberOfInstallments', value); }
        },
        nationalIdentification: {
            get: self => self.$store.state.clickToPayPayment.consumer.nationalIdentification,
            set(value) { this.$store.state.clickToPayPayment.consumer.nationalIdentification = value; }
        },

        installmentList() {
            if (!this.selectedPaymentMethod) {
                return [];
            }
            return this.getInstallmentListForPaymentMethodSelected;
        },

        paymentMethodImage() {
            return this.selectedPaymentMethod ? this.selectedPaymentMethod.Image : null;
        },

        hasCards() {
            const cards = this.$store.state.clickToPayPayment.cards;
            return cards !== null && cards.length > 0;
        },

        cardBrandError() {
            return this.$store.state.clickToPayPayment.cardBrandError;
        },

        isBillingFormInvalid() {
            if(this.sameAsShippingAddress) return false;

            const address = this.billingAddress;
            
            if (this.isNational) {
                return (
                    !address.Street ||
                    !address.State ||
                    !address.City ||
                    !address.Number ||
                    !address.District ||
                    !address.ZipCode
                );
            } else {
                return (
                    !address.Street ||
                    !address.State ||
                    !address.City ||
                    !address.ZipCode
                );
            }
        },

        disableButtonNext() {
            const hasErrors = this.errors.items.length > 0;
            const missingCommonFields = !this.nationalIdentification || !this.agreement || !this.numberOfInstallments;
            const missingAddressFields = this.isBillingFormInvalid;
            
            return missingCommonFields || missingAddressFields || hasErrors;
        },
        
        availableCardBrands() {
            if (this.$store.state.clickToPayPayment.availableCardBrands) {
                return this.$store.state.clickToPayPayment.availableCardBrands;
            }
            return [];
        },

        productHasShipping: () => ProductHelper.productHasShipping(),
    },

    methods: {
        setCardNumber(value) {
            const oldBin = this.cardNumber.substring(0, 8);
            const newBin = value.substring(0, 8);
            this.cardNumber = value;

            if (value && value.length >= 8 && (newBin !== oldBin)) {
                this.doBinQuery();
            }
        },

        async doBinQuery() {
            try {
                let cardBin = this.$store.getters['cardPayment/getCardBin'];
                await this.$store.dispatch('clickToPayPayment/fetchCardBrand', cardBin);
                return true;
            }
            catch (error) {
                logEvent({
                    category: 'BinQuery',
                    action: 'Do Bin Query',
                    label: `[BIN QUERY ERROR]: ${error}`
                });

                const cardBrandError = this.$store.state.clickToPayPayment.cardBrandError;

                await Swal.fire({
                    type: 'error',
                    title: 'Oppsss!',
                    text: cardBrandError ?
                        this.$ml.get(cardBrandError) :
                        this.$ml.get('processingRequestError'),
                    confirmButtonText: this.$ml.get('retry'),
                    cancelButtonText: this.$ml.get('cancel'),
                    showCancelButton: cardBrandError ? false : true,
                    showLoaderOnConfirm: cardBrandError ? false : true,
                    preConfirm: cardBrandError ? null : this.doBinQuery,
                    allowOutsideClick: false,
                    allowEscapeKey: false
                });
                this.cardError = this.$ml.get('numberCardError');
                
                this.cardNumber = '';
                this.$store.state.payment.PaymentMethodId = null;
                return false;
            }
        },

        cancel() {
            this.$store.commit('payment/setNumberOfInstallments', 0);
        },

        isCardExpirationValid() {
            let month = this.$store.state.cardPayment.Expiration.Month || 0;
            let year = this.$store.state.cardPayment.Expiration.Year || 0;

            if (month === 0) return false;
            if (month > 12) return false;

            let today = new Date();
            let cardExpiration = new Date(`20${year}`, month);

            return cardExpiration >= today;
        },

        async finishPayment() {
            if(!this.loading) {
                this.loading = true;
                await this.$store.dispatch('clickToPayPayment/checkoutWithNewCard');
                const responseCode = this.$store.state.clickToPayPayment.checkoutResponse.actionCode;
                if (responseCode !== 'COMPLETE') {
                    this.$store.dispatch('pipelineV2/pushTo', 'ChangePaymentType');
                    this.loading = false;
                    return;
                }

                try {
                    await PaymentProcessor.Process();
                    this.loading = false;

                    this.finishGAEvent();
                } catch (error) {
                    if (error.message === 'cvvRequiredError') {
                        this.showCVVModal = true;
                    }
                    logException(error);
                    this.loading = false;
                }
            }
        },

        showErrorMessage() {
            Swal.fire({
                type: 'error',
                title: this.$ml.get('errorCheckoutHeading'),
                text: this.$ml.get('errorCheckoutBody'),
                confirmButtonText: this.$ml.get('retry'),
                cancelButtonText: this.$ml.get('back'),
                showCancelButton: true,
                allowOutsideClick: false,
                allowEscapeKey: false
            }).then(({ dismiss }) => {
                if (dismiss === 'cancel') {
                    this.$store.dispatch('pipelineV2/pushTo', 'ChangePaymentType');
                }
            });
        },

        goToPreviousStep() {
            this.cancel();
            if (this.hasCards) {
                this.$store.dispatch('pipelineV2/pushTo', 'CardListC2P');
                return;
            }

            this.$store.dispatch('pipelineV2/pushTo', 'UserValidationC2P');
            
            this.$store.state.clickToPayPayment.consumerPresent = false;
            this.$store.state.clickToPayPayment.otpValidation.error = '';
            this.$store.state.clickToPayPayment.consumer.email = '';
        },

        renderMobile() {
            return isMobile();
        },

        closeModal() {
            this.showCVVModal = false;
        },

        async submitCVV() {
            if(!this.loading) {
                this.showCVVModal = false;
                this.loading = true;
                await PaymentProcessor.Process();
                this.loading = false;

                this.finishGAEvent();
            }
        },

        finishGAEvent() {
            logEvent({
                category: 'click_to_pay',
                action: 'finalizacao_cartao_adicionado_c2p',
                label: 'Usuário foi pra tela de adicionar cartão no cadastro C2P e clicou em Concluir compra.'
            });
        }
    },
};
</script>

<style lang="scss" scoped>
    .ucp {
        margin-bottom: 24px;
        
        &__title {
            display: block;
            font-size: 16px;
            font-weight: 700;
            margin-bottom: 16px;
            color: #2F363E;
        }

        &__agreement-container {
            display: flex;
            align-items: center;
            gap: 16px;
            margin: 20px 0 32px;
        }

        &__ba-container {
            margin-bottom: 32px;
            
            @media screen and (min-width: 1025px) {
                margin-bottom: 16px;
            }
        }

    }

    .nct {
        margin-top: 20px;
    }
</style>
